import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DataService from "./../services/data.service";
import { TranslationScope, Translation } from "../types/Translation";
import { useAuth } from "./authContext";
import { StringWithFormat } from '../types/Result';
import FormatUtils from './../utils/FormatUtils.js';

interface TranslationContextValue {
    translate: (key: string | StringWithFormat, context?: string, defaultvalue?: string | StringWithFormat) => Promise<string>
    replaceArguments(translation: string, args: any[]): string
    getContext(): string
    clearTranslationScope(): void
}

const Context = React.createContext<TranslationContextValue | null>(null);

const TranslationProvider: React.FC = (props) => {
    const [scope, setTranslationScope] = useState<TranslationScope>(getTranslationScope());
    const { user } = useAuth();
    const formatUtils = new FormatUtils();

    let location = useLocation();

    function getTranslationScope() {
        const translationScope = localStorage.getItem("translationScope");
        if (translationScope !== null) {
            return JSON.parse(translationScope);
        }

        return {
            languages: {},
            isLoading: false
        };
    }

    function clearTranslationScope() {
        localStorage.removeItem("translationScope");
        if (user)
            loadScope(user.language)
    }

    const translate = async function (key: string | StringWithFormat, context?: string, defaultValue?: string | StringWithFormat): Promise<string> {
        if (!defaultValue)
            defaultValue = key;

        if (typeof key !== 'string')
            key = key.format;

        if (user) {
            if (!context) {
                context = getContext();
            }

            if (typeof defaultValue === 'string') {
                await ensureTranslation(user.language, context, key, user.tenantId, defaultValue)

                let translation = getTranslation(user.language, context, key, user.tenantId);
                return translation;
            }
            else {
                await ensureTranslation(user.language, context, key, user.tenantId, defaultValue.format);

                let translation = getTranslation(user.language, context, key, user.tenantId);

                return replaceArguments(translation, defaultValue.args);
            }
        }

        if (typeof defaultValue === 'string') {
            return defaultValue;
        } else {
            return replaceArguments(defaultValue.format, defaultValue.args);
        }
    }

    function replaceArguments(translation: string, args: any[]) : string {
        for (var i = 0; i < args.length; i++) {
            if (translation.indexOf('{' + i + '}') >= 0) {
                translation = translation.replace('{' + i + '}', args[i]);
            }
            else if (translation.indexOf('{' + i + ':dd-MM-yyyy}') >= 0) {
                translation = translation.replace('{' + i + ':dd-MM-yyyy}', formatUtils.formatDateStringFromLocale(args[i], user?.language));
            }
            else if (translation.indexOf('{' + i + ':dd-MM-yyyy HH:mm}') >= 0) {
                translation = translation.replace('{' + i + ':dd-MM-yyyy HH:mm}', formatUtils.formatDateTimeStringFromLocale(args[i], user?.language));
            }
        }
        return translation;
    }

    function getContext(): string {
        let context = location.pathname;

        let found = false;
        do {
            found = false;
            let index = context.lastIndexOf('/');
            if (index >= 0) {
                let part = context.substr(index + 1);
                if (!isNaN(Number(part))) {
                    context = context.substr(0, index);
                    found = true;
                }
            }
        } while (found);

        if (context.substr(0, 5) === '/plm/')
            context = context.substr(4);

        return context
    }

    async function ensureTranslation(language: string, context: string, key: string, tenantId: number, defaultValue: string) {
        await ensureScope(language);
        if (!hasTranslation(language, context, key, tenantId))
            await addMissingTranslation(language, context, key, defaultValue);
    }

    async function ensureScope(language: string): Promise<void> {
        // 7 days expiry date, invalidate cache
        const expiryDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        if (scope.loadedOn && new Date(scope.loadedOn) < expiryDate) {
            scope.loadedOn = undefined
            scope.isLoading = false

            localStorage.setItem('translationScope', JSON.stringify(scope))
            setTranslationScope(scope)
        }

        if (!scope.languages[language]) {
            scope.languages[language] = { contexts: {} }

            scope.loadedOn = undefined
            scope.isLoading = false

            localStorage.setItem('translationScope', JSON.stringify(scope))
            setTranslationScope(scope)
        }

        if (!scope.loadedOn && scope.isLoading === false) await loadScope(language)
    }

    async function loadScope(language: string): Promise<void> {
        console.log('Loading translation scope')
        scope.isLoading = true

        localStorage.setItem('translationScope', JSON.stringify(scope))
        setTranslationScope(scope)

        await DataService.get<Translation[]>(`api/v1/translations/bylanguage?language=` + language)
            .then((translations) => {
                scope.languages[language] = { contexts: {} }
                setTranslationScope(scope)

                addTranslations(scope, translations)

                scope.loadedOn = new Date(Date.now())
                scope.isLoading = false
                localStorage.setItem('translationScope', JSON.stringify(scope))
                setTranslationScope(scope)
                console.log('Translation scope loaded')

            })

    }

    function addTranslations(tscope: TranslationScope, translations: Translation[]): void {
        translations.forEach((translation) => {
            addTranslation(tscope, translation)
        })
    }

    function addTranslation(tscope:TranslationScope, translation: Translation): void {
        const tenantId = translation.tenantId ? translation.tenantId : 0
        const key = translation.translationKey
        const text = translation.translatedText
        const { language, context } = translation

        if (!tscope.languages[language])
            tscope.languages[language] = { contexts: {} }

        if (!tscope.languages[language].contexts[context])
            tscope.languages[language].contexts[context] = { keys: {} }

        if (!tscope.languages[language].contexts[context].keys[key])
            tscope.languages[language].contexts[context].keys[key] = { tenants: {} }

        tscope.languages[language].contexts[context].keys[key].tenants[tenantId] = text
    }

    function hasTranslation(language: string, context: string, key: string, tenantId: number) {
        if (!scope.languages[language]) return false;
        if (!scope.languages[language].contexts[context]) return false;
        if (!scope.languages[language].contexts[context].keys[key]) return false;
        if (scope.languages[language].contexts[context].keys[key].tenants[tenantId]) return true;
        if (scope.languages[language].contexts[context].keys[key].tenants[0]) return true;

        return false;
    }

    async function addMissingTranslation(language: string, context: string, key: string, defaultValue: string) {
        let missingTranslation = { context, key, defaultValue, language };
        await DataService.post<Translation>('api/v1/translations/addMissing', missingTranslation)
            .then(response => {
                addTranslation(scope, response)
                localStorage.setItem('translationScope', JSON.stringify(scope));
                setTranslationScope(scope);
            })
            .catch();
    }

    function getTranslation(language: string, context: string, key: string, tenantId: number): string {
        if (scope.languages[language].contexts[context].keys[key].tenants[tenantId])
            return scope.languages[language].contexts[context].keys[key].tenants[tenantId];

        return scope.languages[language].contexts[context].keys[key].tenants[0];
    }

    return <Context.Provider value={{ translate, replaceArguments, getContext, clearTranslationScope }} {...props} />;
};

function useTranslations(): TranslationContextValue {
    const context = React.useContext(Context);
    if (context === null) {
        throw new Error('useTranslations must be used within a TranslationProvider');
    }
    return context;
}

export { TranslationProvider, useTranslations };